import { AGGClient } from "../../../../clients/agg-client/agg-client";

import {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useState
} from "react";

import Cookies from "js-cookie";
import {
    useCategories,
    useTemplates
} from "@/content/templates/template.provider";
import type { Category, Template } from "@/content/templates/template.types";
import {
    RECO_TEMPLATE_LOCAL_STORAGE_NAME,
    TEMPLATE_CATEGORY_OBJ
} from "@/utils/content-queries/constants";
import { getRecommendedTemplates } from "@/utils/content-queries/get-content-data";

import { getLocalStorage, setLocalStorage } from "@/utils/localstorage";

export const SHOW_RECOMMENDED_TEMPLATES_PATH = "/software/confluence/templates";

type RecommendedTemplatesContextType = {
    enrolledInRecommended: boolean;
    recommendedTemplates: Template[];
    recommendedTemplateIds?: string[];
    recommendedTemplatesStatus: "pending" | "resolved" | "rejected" | null;
    adjustedCategories: Category[];
};

const RecommendedTemplatesContext =
    createContext<RecommendedTemplatesContextType>({
        enrolledInRecommended: false,
        recommendedTemplates: [],
        recommendedTemplateIds: [],
        recommendedTemplatesStatus: null,
        adjustedCategories: []
    });

export const useRecommendedTemplates = () => {
    const context = useContext(RecommendedTemplatesContext);
    if (!context) {
        throw new Error(
            "useRecommendedTemplates must be used within a RecommendedTemplatesProvider"
        );
    }
    return context;
};

export const RecommendedTemplatesProvider = ({
    children
}: PropsWithChildren) => {
    const templateCategories = useCategories();
    const templates = useTemplates();
    const [adjustedCategories, setAdjustedCategories] =
        useState<Category[]>(templateCategories);
    const [recommendedTemplates, setRecommendedTemplates] = useState<
        Template[]
    >([]);
    const [recommendedTemplateIds, setRecommendedTemplateIds] = useState<
        string[]
    >([]);
    const [recoTemplatePromiseStatus, setRecoTemplatePromiseStatus] = useState<
        "pending" | "resolved" | "rejected" | null
    >(null);

    const term = location.pathname;

    const showRecommendedTemplates =
        term === SHOW_RECOMMENDED_TEMPLATES_PATH &&
        // Request has finished, and you have recommendations
        recoTemplatePromiseStatus === "resolved" &&
        recommendedTemplateIds.length > 0;

    useEffect(() => {
        if (showRecommendedTemplates) {
            // remove featured and add recommendations
            const updatedCategories = templateCategories.filter(
                (category) => category.urlSlug !== "recommended"
            );
            updatedCategories.unshift(TEMPLATE_CATEGORY_OBJ);
            setAdjustedCategories(updatedCategories);
        } else {
            setAdjustedCategories(templateCategories);
        }
    }, [templateCategories, showRecommendedTemplates]);

    // Update template categories with recommended category
    useEffect(() => {
        for (const template of templates) {
            if (template.category) {
                const index = template.category.findIndex(
                    (category) => category.id === TEMPLATE_CATEGORY_OBJ.id
                );
                if (index > -1) {
                    template.category.splice(index, 1);
                }
            }
            if (recommendedTemplateIds.includes(template.templateId)) {
                if (!template.category) {
                    template.category = [TEMPLATE_CATEGORY_OBJ];
                } else {
                    template.category.push(TEMPLATE_CATEGORY_OBJ);
                }
            }
        }
    }, [templates, recommendedTemplateIds]);

    useEffect(() => {
        if (term === SHOW_RECOMMENDED_TEMPLATES_PATH) {
            const aggClient = new AGGClient({
                gatewayUrl:
                    process.env["NODE_ENV"] === "production"
                        ? "https://www.atlassian.com/gateway/api/graphql"
                        : "https://wac.stg.internal.atlassian.com/gateway/api/graphql"
            });

            let recommendedTemplateIds;
            try {
                recommendedTemplateIds = JSON.parse(
                    getLocalStorage(RECO_TEMPLATE_LOCAL_STORAGE_NAME) as string
                );
            } catch (error) {
                console.log(`Error parsing localstorage ${error}`);
            }
            let anonymousId = "";
            try {
                anonymousId = JSON.parse(
                    decodeURIComponent(Cookies.get("ajs_anonymous_id") || `""`)
                );
            } catch (error) {
                console.log(`Error parsing anonymousId ${error}`);
                anonymousId = Cookies.get("ajs_anonymous_id") || "";
            }

            const url = new URL(window.location.href);
            const paidSearchTerm = url.searchParams.get("keyword");

            const userId = Cookies.get("__aid_user_id") || "";
            setRecoTemplatePromiseStatus("pending");
            getRecommendedTemplates({
                anonymousId,
                userId,
                aggClient,
                recommendedTemplateIds,
                custom: {
                    paidSearchTerm
                }
            })
                .then((recoTemplates) => {
                    setRecommendedTemplateIds(
                        recoTemplates.recommendedTemplateIds
                    );
                    setRecommendedTemplates(recoTemplates.templates);
                    setRecoTemplatePromiseStatus("resolved");
                    setLocalStorage(
                        RECO_TEMPLATE_LOCAL_STORAGE_NAME,
                        JSON.stringify(recoTemplates.recommendedTemplateIds)
                    );
                })
                .catch((err) => {
                    setRecoTemplatePromiseStatus("rejected");
                    console.log(err);
                });
        }
    }, []);

    return (
        <RecommendedTemplatesContext.Provider
            value={{
                enrolledInRecommended: term === SHOW_RECOMMENDED_TEMPLATES_PATH,
                recommendedTemplates,
                recommendedTemplateIds,
                recommendedTemplatesStatus: recoTemplatePromiseStatus,
                adjustedCategories
            }}
        >
            {children}
        </RecommendedTemplatesContext.Provider>
    );
};
